import { useMemo } from "react";
import {
  AlertCircle,
  ArrowRight,
  Car,
  Clock,
  Leaf,
  Loader2,
  MapPin,
  Search,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { LocationAutocomplete } from "@/components/common/PlaceAutocompleteInput";
import { useGuestCommuteCalculator } from "@/hooks/useGuestCommuteCalculator";
import {
  AUTO_FARE_RATES,
  CAB_FARE_RATES,
  computeSavings,
  estimateCo2SavedKg,
  estimateFare,
} from "@/utils/commuteFareComparison";
import { getCurrencySymbol } from "@/utils/currency";
import { cn } from "@/lib/utils";

interface GuestCommuteContentProps {
  idPrefix?: string;
}

function formatAmount(value: number): string {
  return value.toLocaleString("en-IN");
}

interface FareOptionCardProps {
  label: string;
  caption: string;
  currencySymbol: string;
  amount: number;
  icon: React.ReactNode;
  highlight?: boolean;
  badge?: string;
}

function FareOptionCard({
  label,
  caption,
  currencySymbol,
  amount,
  icon,
  highlight,
  badge,
}: FareOptionCardProps) {
  return (
    <div
      className={cn(
        "relative flex flex-col rounded-2xl border p-5",
        highlight
          ? "border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-background shadow-md ring-1 ring-primary/20"
          : "border-border/70 bg-card"
      )}
    >
      {badge && (
        <span className="absolute -top-3 right-4 rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground shadow">
          {badge}
        </span>
      )}
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-lg",
            highlight ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
          )}
        >
          {icon}
        </span>
        <div>
          <p className="text-sm font-bold text-foreground">{label}</p>
          <p className="text-xs text-muted-foreground">{caption}</p>
        </div>
      </div>
      <p
        className={cn(
          "mt-4 text-3xl font-bold tracking-tight",
          highlight ? "text-primary" : "text-foreground"
        )}
      >
        {currencySymbol}
        {formatAmount(amount)}
      </p>
    </div>
  );
}

export function GuestCommuteContent({ idPrefix = "guest" }: GuestCommuteContentProps) {
  const {
    fromPlace,
    toPlace,
    destinationKey,
    isDetectingLocation,
    validationError,
    fareResult,
    showResults,
    routeEstimate,
    routeEstimateLoading,
    isCalculating,
    clearValidationError,
    handleFromSelect,
    handleToSelect,
    handleClearFrom,
    handleClearTo,
    handleCalculate,
  } = useGuestCommuteCalculator();

  const comparison = useMemo(() => {
    if (!fareResult) return null;

    const { perSeatPricing } = fareResult.fareCalculation;
    const distanceKm =
      routeEstimate?.distanceKm ?? perSeatPricing.totalKilometers ?? 0;
    const duriFare = perSeatPricing.suggestedFare;
    const cabFare = estimateFare(distanceKm, CAB_FARE_RATES);
    const autoFare = estimateFare(distanceKm, AUTO_FARE_RATES);
    const co2PerTripKg = estimateCo2SavedKg(distanceKm);

    return {
      currencySymbol: getCurrencySymbol(perSeatPricing.currency),
      duriFare,
      cabFare,
      autoFare,
      cabSavings: computeSavings(cabFare, duriFare),
      autoSavings: computeSavings(autoFare, duriFare),
      co2PerTripKg,
      co2WeeklyKg: Math.round(co2PerTripKg * 2 * 5 * 10) / 10,
    };
  }, [fareResult, routeEstimate]);

  return (
    <div className="space-y-6">
      <div className="space-y-5">
        <div className="grid gap-5 sm:grid-cols-2">
          <LocationAutocomplete
            id={`${idPrefix}-from`}
            label="From (Home / Pickup)"
            placeholder={
              isDetectingLocation
                ? "Detecting your current location..."
                : "Search starting location"
            }
            mode="origin"
            value={fromPlace}
            isLoading={isDetectingLocation}
            disabled={isDetectingLocation}
            onPlaceSelect={handleFromSelect}
            onClear={handleClearFrom}
            onInteraction={clearValidationError}
          />
          <LocationAutocomplete
            key={`${idPrefix}-to-${destinationKey}`}
            id={`${idPrefix}-to`}
            label="To (Office / Destination)"
            placeholder="Search destination"
            mode="destination"
            value={toPlace}
            restrictToCountryCode={fromPlace?.countryCode}
            onPlaceSelect={handleToSelect}
            onClear={handleClearTo}
            onInteraction={clearValidationError}
          />
        </div>

        {validationError && (
          <Alert variant="destructive" className="border-destructive/30">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm leading-relaxed">
              {validationError}
            </AlertDescription>
          </Alert>
        )}

        <Button
          type="button"
          size="lg"
          className="h-12 w-full gap-2 text-base font-semibold shadow-md"
          disabled={isCalculating}
          onClick={handleCalculate}
        >
          {isCalculating ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Calculating your savings...
            </>
          ) : (
            <>
              <Search className="h-5 w-5" />
              Check your savings now
            </>
          )}
        </Button>
      </div>

      {showResults && comparison && (
        <div className="space-y-6">
          {(routeEstimate || routeEstimateLoading) && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-primary" />
                {routeEstimateLoading
                  ? "Measuring distance..."
                  : `${routeEstimate?.distanceText} route`}
              </span>
              {routeEstimate?.durationText && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-primary" />
                  {routeEstimate.durationText} drive
                </span>
              )}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <FareOptionCard
              label="Auto"
              caption="Solo, one way"
              currencySymbol={comparison.currencySymbol}
              amount={comparison.autoFare}
              icon={<Car className="h-5 w-5" />}
            />
            <FareOptionCard
              label="Cab / Uber"
              caption="Solo, one way"
              currencySymbol={comparison.currencySymbol}
              amount={comparison.cabFare}
              icon={<Car className="h-5 w-5" />}
            />
            <FareOptionCard
              label="Duri"
              caption="Per seat, shared"
              currencySymbol={comparison.currencySymbol}
              amount={comparison.duriFare}
              icon={<Sparkles className="h-5 w-5" />}
              highlight
              badge="Best value"
            />
          </div>

          {comparison.cabSavings.amount > 0 && (
            <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-background p-6 ring-1 ring-primary/15">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <TrendingUp className="h-6 w-6 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg font-bold text-foreground">
                    Save {comparison.currencySymbol}
                    {formatAmount(comparison.cabSavings.amount)} every trip with Duri
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    That's about{" "}
                    <span className="font-semibold text-primary">
                      {comparison.cabSavings.percent}% cheaper
                    </span>{" "}
                    than a solo cab and{" "}
                    <span className="font-semibold text-primary">
                      {comparison.autoSavings.percent}% cheaper
                    </span>{" "}
                    than an auto — and on a 5-day week that's around{" "}
                    <span className="font-semibold text-foreground">
                      {comparison.currencySymbol}
                      {formatAmount(comparison.cabSavings.amount * 2 * 5)}
                    </span>{" "}
                    saved versus cabs (round trips).
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <p className="text-center text-sm font-semibold text-foreground">
              And it's not just your wallet — every shared trip helps the city too
            </p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <div className="flex items-start gap-3 rounded-2xl border border-border/70 bg-card p-5">
                <div className="h-fit rounded-lg bg-primary/10 p-2.5">
                  <Leaf className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">
                    ~{comparison.co2PerTripKg} kg CO₂ saved per trip
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    That's roughly{" "}
                    <span className="font-semibold text-foreground">
                      {comparison.co2WeeklyKg} kg a week
                    </span>{" "}
                    kept out of the air by not driving this route solo.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-border/70 bg-card p-5">
                <div className="h-fit rounded-lg bg-primary/10 p-2.5">
                  <Car className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">
                    One less car on the road
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Sharing seats means fewer vehicles on your corridor — less peak-hour
                    congestion and easier parking for everyone.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <a
            href="https://duriapp.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground shadow-md transition-transform hover:-translate-y-0.5"
          >
            Start saving — get the Duri app
            <ArrowRight className="h-4 w-4" />
          </a>

          <p className="text-center text-xs text-muted-foreground">
            Cab and auto fares are indicative estimates for comparison. Duri's per-seat
            price is calculated for your exact route.
          </p>
        </div>
      )}
    </div>
  );
}
